import React, { useState, useEffect } from 'react';
import {
  Wallet,
  RefreshCw,
  Eye,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Search,
  X,
  Ban,
  Unlock,
  Receipt,
  Users,
  TrendingUp,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Mail
} from 'lucide-react';
import './POSSettlement.css';

const POSSettlement = () => {
  const [merchants, setMerchants] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('merchants');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantDetail, setMerchantDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    hasOverdue: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [invoicePagination, setInvoicePagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });
  const [invoiceFilters, setInvoiceFilters] = useState({
    status: '',
    merchantId: ''
  });

  // Transaction states
  const [transactions, setTransactions] = useState([]);
  const [transactionPagination, setTransactionPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });
  const [transactionFilters, setTransactionFilters] = useState({
    type: '',
    merchantId: '',
    period: 'month',
    startDate: '',
    endDate: ''
  });
  const [transactionSummary, setTransactionSummary] = useState(null);

  // Action modal states
  const [actionNotes, setActionNotes] = useState('');
  const [waiveReason, setWaiveReason] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    if (activeTab === 'merchants') {
      loadMerchants();
    } else if (activeTab === 'invoices') {
      loadInvoices();
    } else if (activeTab === 'transactions') {
      loadTransactions();
    }
  }, [activeTab, pagination.current, filters, invoicePagination.current, invoiceFilters, transactionPagination.current, transactionFilters]);

  const loadOverview = async () => {
    try {
      const response = await fetch('/api/admin/pos-fees/overview', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.overview) {
        setOverview({
          totalMerchants: data.overview.merchantsWithBalance || 0,
          totalOutstanding: data.overview.totalOutstanding || 0,
          overdueInvoices: data.overview.overdueInvoices?.count || 0,
          overdueAmount: data.overview.overdueInvoices?.amount || 0,
          pendingInvoices: data.overview.pendingInvoices?.count || 0,
          pendingAmount: data.overview.pendingInvoices?.amount || 0,
          blockedMerchants: data.overview.topDebtors?.filter(d => d.isBlocked)?.length || 0,
          totalCollected: data.overview.totalPaid || 0,
          totalAccrued: data.overview.totalAccrued || 0,
          totalWaived: data.overview.totalWaived || 0
        });
      }
    } catch (err) {
      console.error('Failed to load overview:', err);
    }
  };

  const loadMerchants = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: pagination.current,
        limit: 20,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.hasOverdue && { hasOverdue: filters.hasOverdue })
      });

      const response = await fetch(`/api/admin/pos-fees/merchants?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setMerchants(data.merchants || []);
        if (data.pagination) {
          setPagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.error || 'Failed to load merchants');
      }
    } catch (err) {
      setError('Failed to load merchants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: invoicePagination.current,
        limit: 20,
        ...(invoiceFilters.status && { status: invoiceFilters.status }),
        ...(invoiceFilters.merchantId && { merchantId: invoiceFilters.merchantId })
      });

      const response = await fetch(`/api/admin/pos-fees/invoices?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setInvoices(data.invoices || []);
        if (data.pagination) {
          setInvoicePagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.error || 'Failed to load invoices');
      }
    } catch (err) {
      setError('Failed to load invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: transactionPagination.current,
        limit: 50,
        ...(transactionFilters.type && { type: transactionFilters.type }),
        ...(transactionFilters.merchantId && { merchantId: transactionFilters.merchantId }),
        ...(transactionFilters.period && transactionFilters.period !== 'custom' && { period: transactionFilters.period }),
        ...(transactionFilters.period === 'custom' && transactionFilters.startDate && { startDate: transactionFilters.startDate }),
        ...(transactionFilters.period === 'custom' && transactionFilters.endDate && { endDate: transactionFilters.endDate })
      });

      const response = await fetch(`/api/admin/pos-fees/transactions?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setTransactions(data.transactions || []);
        setTransactionSummary(data.summary || null);
        if (data.pagination) {
          setTransactionPagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.error || 'Failed to load transactions');
      }
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMerchantDetail = async (merchantId) => {
    try {
      const response = await fetch(`/api/admin/pos-fees/merchants/${merchantId}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setMerchantDetail(data);
        setSelectedMerchant(merchantId);
        setModalType('detail');
        setShowModal(true);
      } else {
        setError(data.error || 'Failed to load merchant details');
      }
    } catch (err) {
      setError('Failed to load merchant details');
      console.error(err);
    }
  };

  const handleBlockMerchant = async (merchantId) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/pos-fees/merchants/${merchantId}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: actionNotes || 'Overdue POS fees' })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Merchant POS access blocked successfully');
        setShowModal(false);
        loadMerchants();
        loadOverview();
      } else {
        setError(data.error || 'Failed to block merchant');
      }
    } catch (err) {
      setError('Failed to block merchant');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockMerchant = async (merchantId) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/pos-fees/merchants/${merchantId}/unblock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: actionNotes })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Merchant POS access restored successfully');
        setShowModal(false);
        loadMerchants();
        loadOverview();
      } else {
        setError(data.error || 'Failed to unblock merchant');
      }
    } catch (err) {
      setError('Failed to unblock merchant');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleWaiveInvoice = async (invoiceId) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/pos-fees/invoices/${invoiceId}/waive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: waiveReason })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Invoice waived successfully');
        setShowModal(false);
        loadInvoices();
        loadOverview();
      } else {
        setError(data.error || 'Failed to waive invoice');
      }
    } catch (err) {
      setError('Failed to waive invoice');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/pos-fees/invoices/${invoiceId}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          paymentRef: actionNotes,
          paymentMethod: 'MANUAL'
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Invoice marked as paid successfully');
        setShowModal(false);
        loadInvoices();
        loadOverview();
      } else {
        setError(data.error || 'Failed to mark invoice as paid');
      }
    } catch (err) {
      setError('Failed to mark invoice as paid');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateInvoices = async () => {
    try {
      setActionLoading(true);
      const response = await fetch('/api/admin/pos-fees/invoices/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(data.message || `Generated ${data.generated || 0} invoice(s) successfully`);
        loadInvoices();
        loadOverview();
      } else {
        setError(data.error || 'Failed to generate invoices');
      }
    } catch (err) {
      setError('Failed to generate invoices');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  // Force-invoice a single merchant. Bypasses the cycle schedule
  // (nextInvoiceAt) — useful when the schedule is wrong or a merchant
  // is stuck. The backend may still respond with "Auto-paid from
  // wallet; no invoice needed" if the wallet absorbed all the fees.
  const handleGenerateInvoiceForMerchant = async (merchant) => {
    const label = merchant.merchantName || merchant.email || merchant.merchantId;
    const confirmMsg =
      `Force-generate an invoice for ${label} now?\n\n` +
      `Bypasses the schedule. If their wallet has already absorbed the fees, ` +
      `no invoice will be created and you'll see an auto-pay message instead.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setActionLoading(true);
      const response = await fetch('/api/admin/pos-fees/invoices/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ merchantId: merchant.merchantId }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        if (data.autopaid && !data.invoice) {
          setSuccess(`${label}: ${data.message}`);
        } else if (data.invoice) {
          setSuccess(`${label}: ${data.message} (${data.invoice.invoiceNumber})`);
        } else {
          setSuccess(`${label}: ${data.message || 'Invoice generated'}`);
        }
        loadMerchants();
        loadInvoices();
        loadOverview();
      } else {
        setError(`${label}: ${data.error || 'Failed to generate invoice'}`);
      }
    } catch (err) {
      setError(`${label}: failed to generate invoice`);
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunOverdueCheck = async () => {
    try {
      setActionLoading(true);
      const response = await fetch('/api/admin/pos-fees/overdue-check?autoBlock=false', {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(`Found ${data.overdueCount || 0} overdue invoices`);
        loadMerchants();
        loadOverview();
      } else {
        setError(data.error || 'Failed to run overdue check');
      }
    } catch (err) {
      setError('Failed to run overdue check');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openActionModal = (type, item = null) => {
    setModalType(type);
    setSelectedMerchant(item);
    setActionNotes('');
    setWaiveReason('');
    setReminderMessage('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedMerchant(null);
    setMerchantDetail(null);
    setActionNotes('');
    setWaiveReason('');
    setReminderMessage('');
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/pos-fees/invoices/${invoiceId}/send-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customMessage: reminderMessage.trim() || undefined }),
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(data.message || 'Reminder sent');
        setShowModal(false);
        loadInvoices();
      } else {
        setError(data.error || 'Failed to send reminder');
      }
    } catch (err) {
      setError('Failed to send reminder');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatusBadge = (status, isBlocked = false) => {
    if (isBlocked) {
      return <span className="poss-badge poss-badge-blocked"><Ban size={12} /> Blocked</span>;
    }

    const statusConfig = {
      PENDING: { class: 'poss-badge-pending', icon: <Clock size={12} />, label: 'Pending' },
      PAID: { class: 'poss-badge-paid', icon: <CheckCircle2 size={12} />, label: 'Paid' },
      OVERDUE: { class: 'poss-badge-overdue', icon: <AlertCircle size={12} />, label: 'Overdue' },
      WAIVED: { class: 'poss-badge-paid', icon: <CheckCircle2 size={12} />, label: 'Waived' },
      CANCELLED: { class: 'poss-badge-overdue', icon: <XCircle size={12} />, label: 'Cancelled' }
    };

    const config = statusConfig[status] || { class: 'poss-badge-pending', icon: null, label: status };
    return <span className={`poss-badge ${config.class}`}>{config.icon} {config.label}</span>;
  };

  return (
    <div className="poss-container">
      <div className="poss-header">
        <h1><Wallet /> POS Fee Settlement</h1>
        <div className="poss-header-actions">
          <button
            className="poss-btn poss-btn-secondary"
            onClick={handleRunOverdueCheck}
            disabled={actionLoading}
          >
            <AlertTriangle size={16} />
            Check Overdue
          </button>
          <button
            className="poss-btn poss-btn-primary"
            onClick={handleGenerateInvoices}
            disabled={actionLoading}
          >
            <Receipt size={16} />
            Generate Invoices
          </button>
          <button className="poss-btn poss-btn-secondary" onClick={() => { loadOverview(); activeTab === 'merchants' ? loadMerchants() : loadInvoices(); }}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="poss-alert poss-alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button className="poss-btn poss-btn-icon" onClick={() => setError('')}><X size={16} /></button>
        </div>
      )}

      {success && (
        <div className="poss-alert poss-alert-success">
          <CheckCircle2 size={18} />
          <span>{success}</span>
          <button className="poss-btn poss-btn-icon" onClick={() => setSuccess('')}><X size={16} /></button>
        </div>
      )}

      {/* Overview Stats */}
      {overview && (
        <div className="poss-stats-grid">
          <div className="poss-stat-card">
            <div className="poss-stat-label"><Users size={16} /> Total Merchants</div>
            <div className="poss-stat-value">{overview.totalMerchants}</div>
            <div className="poss-stat-subtext">With POS fee balance</div>
          </div>
          <div className="poss-stat-card poss-stat-warning">
            <div className="poss-stat-label"><Wallet size={16} /> Outstanding Balance</div>
            <div className="poss-stat-value">{formatCurrency(overview.totalOutstanding)}</div>
            <div className="poss-stat-subtext">Across all merchants</div>
          </div>
          <div className="poss-stat-card poss-stat-danger">
            <div className="poss-stat-label"><AlertCircle size={16} /> Overdue Invoices</div>
            <div className="poss-stat-value">{overview.overdueInvoices}</div>
            <div className="poss-stat-subtext">{formatCurrency(overview.overdueAmount)} total</div>
          </div>
          <div className="poss-stat-card">
            <div className="poss-stat-label"><Ban size={16} /> Blocked Merchants</div>
            <div className="poss-stat-value">{overview.blockedMerchants}</div>
            <div className="poss-stat-subtext">POS access revoked</div>
          </div>
          <div className="poss-stat-card poss-stat-success">
            <div className="poss-stat-label"><TrendingUp size={16} /> Total Collected</div>
            <div className="poss-stat-value">{formatCurrency(overview.totalCollected)}</div>
            <div className="poss-stat-subtext">All time</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="poss-tabs">
        <button
          className={`poss-tab ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          Merchants
        </button>
        <button
          className={`poss-tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        <button
          className={`poss-tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transaction Logs
        </button>
      </div>

      {/* Merchants Tab */}
      {activeTab === 'merchants' && (
        <>
          <div className="poss-filters-section">
            <div className="poss-filters-row">
              <div className="poss-filter-group">
                <label>Search</label>
                <div className="poss-search-wrapper">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search merchant..."
                    className="poss-search-input"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>
              <div className="poss-filter-group">
                <label>Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All Merchants</option>
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
              <div className="poss-filter-group">
                <label>Has Overdue</label>
                <select
                  value={filters.hasOverdue}
                  onChange={(e) => setFilters({ ...filters, hasOverdue: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          </div>

          <div className="poss-table-container">
            {loading ? (
              <div className="poss-loading-spinner">
                <RefreshCw size={24} className="spin" />
              </div>
            ) : merchants.length === 0 ? (
              <div className="poss-empty-state">
                <Users size={48} />
                <p>No merchants with POS fee balance found</p>
              </div>
            ) : (
              <>
                <table className="poss-data-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Outstanding Balance</th>
                      <th>Total Accrued</th>
                      <th>Total Paid</th>
                      <th>Overdue Invoices</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merchants.map((merchant) => (
                      <tr key={merchant.id}>
                        <td>
                          <div className="poss-merchant-cell">
                            <span className="poss-merchant-name">{merchant.merchantName || merchant.email}</span>
                            <span className="poss-merchant-email">{merchant.email}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`poss-amount ${merchant.balance > 0 ? 'positive' : 'zero'}`}>
                            {formatCurrency(merchant.balance)}
                          </span>
                        </td>
                        <td>{formatCurrency(merchant.totalAccrued)}</td>
                        <td>{formatCurrency(merchant.totalPaid)}</td>
                        <td>{merchant.hasOverdue ? <span className="poss-badge poss-badge-overdue">Yes</span> : <span className="poss-badge poss-badge-paid">No</span>}</td>
                        <td>
                          {merchant.isBlocked ? (
                            <span className="poss-badge poss-badge-blocked"><Ban size={12} /> Blocked</span>
                          ) : (
                            <span className="poss-badge poss-badge-active"><CheckCircle2 size={12} /> Active</span>
                          )}
                        </td>
                        <td>
                          <div className="poss-action-buttons">
                            <button
                              className="poss-btn poss-btn-sm poss-btn-secondary"
                              onClick={() => loadMerchantDetail(merchant.merchantId)}
                            >
                              <Eye size={14} />
                              View
                            </button>
                            <button
                              className="poss-btn poss-btn-sm poss-btn-primary"
                              onClick={() => handleGenerateInvoiceForMerchant(merchant)}
                              disabled={merchant.balance <= 0 || actionLoading}
                              title={merchant.balance <= 0 ? 'No outstanding balance' : 'Force-generate an invoice now (bypasses schedule)'}
                            >
                              <Receipt size={14} />
                              Invoice
                            </button>
                            {merchant.isBlocked ? (
                              <button
                                className="poss-btn poss-btn-sm poss-btn-success"
                                onClick={() => openActionModal('unblock', merchant)}
                              >
                                <Unlock size={14} />
                                Unblock
                              </button>
                            ) : (
                              <button
                                className="poss-btn poss-btn-sm poss-btn-danger"
                                onClick={() => openActionModal('block', merchant)}
                                disabled={merchant.balance <= 0}
                              >
                                <Ban size={14} />
                                Block
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pagination.pages > 1 && (
                  <div className="poss-pagination">
                    <span className="poss-pagination-info">
                      Showing {((pagination.current - 1) * 20) + 1} - {Math.min(pagination.current * 20, pagination.total)} of {pagination.total} merchants
                    </span>
                    <div className="poss-pagination-buttons">
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setPagination({ ...pagination, current: 1 })}
                        disabled={pagination.current <= 1}
                        title="First page"
                      >
                        <ChevronsLeft size={16} />
                      </button>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setPagination({ ...pagination, current: pagination.current - 1 })}
                        disabled={pagination.current <= 1}
                        title="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span className="poss-pagination-current">
                        Page {pagination.current} of {pagination.pages}
                      </span>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setPagination({ ...pagination, current: pagination.current + 1 })}
                        disabled={pagination.current >= pagination.pages}
                        title="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setPagination({ ...pagination, current: pagination.pages })}
                        disabled={pagination.current >= pagination.pages}
                        title="Last page"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <>
          <div className="poss-filters-section">
            <div className="poss-filters-row">
              <div className="poss-filter-group">
                <label>Status</label>
                <select
                  value={invoiceFilters.status}
                  onChange={(e) => setInvoiceFilters({ ...invoiceFilters, status: e.target.value })}
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="PAID">Paid</option>
                  <option value="WAIVED">Waived</option>
                </select>
              </div>
            </div>
          </div>

          <div className="poss-table-container">
            {loading ? (
              <div className="poss-loading-spinner">
                <RefreshCw size={24} className="spin" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="poss-empty-state">
                <Receipt size={48} />
                <p>No invoices found</p>
              </div>
            ) : (
              <>
                <table className="poss-data-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Merchant</th>
                      <th>Amount</th>
                      <th>Period</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td><strong>{invoice.invoiceNumber}</strong></td>
                        <td>
                          <div className="poss-merchant-cell">
                            <span className="poss-merchant-name">{invoice.merchantName || invoice.merchantEmail}</span>
                            <span className="poss-merchant-email">{invoice.merchantEmail}</span>
                          </div>
                        </td>
                        <td>
                          <span className="poss-amount positive">{formatCurrency(invoice.amount)}</span>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.8rem' }}>
                            {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                          </div>
                        </td>
                        <td>{formatDate(invoice.dueDate)}</td>
                        <td>{renderStatusBadge(invoice.status)}</td>
                        <td>
                          <div className="poss-action-buttons">
                            {(invoice.status === 'PENDING' || invoice.status === 'OVERDUE') && (
                              <>
                                <button
                                  className="poss-btn poss-btn-sm poss-btn-primary"
                                  onClick={() => openActionModal('sendReminder', invoice)}
                                  title="Email reminder to merchant"
                                >
                                  <Mail size={14} />
                                  Nudge
                                </button>
                                <button
                                  className="poss-btn poss-btn-sm poss-btn-success"
                                  onClick={() => openActionModal('markPaid', invoice)}
                                >
                                  <CheckCircle2 size={14} />
                                  Mark Paid
                                </button>
                                <button
                                  className="poss-btn poss-btn-sm poss-btn-warning"
                                  onClick={() => openActionModal('waive', invoice)}
                                >
                                  <XCircle size={14} />
                                  Waive
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {invoicePagination.pages > 1 && (
                  <div className="poss-pagination">
                    <span className="poss-pagination-info">
                      Showing {((invoicePagination.current - 1) * 20) + 1} - {Math.min(invoicePagination.current * 20, invoicePagination.total)} of {invoicePagination.total} invoices
                    </span>
                    <div className="poss-pagination-buttons">
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setInvoicePagination({ ...invoicePagination, current: 1 })}
                        disabled={invoicePagination.current <= 1}
                        title="First page"
                      >
                        <ChevronsLeft size={16} />
                      </button>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setInvoicePagination({ ...invoicePagination, current: invoicePagination.current - 1 })}
                        disabled={invoicePagination.current <= 1}
                        title="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span className="poss-pagination-current">
                        Page {invoicePagination.current} of {invoicePagination.pages}
                      </span>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setInvoicePagination({ ...invoicePagination, current: invoicePagination.current + 1 })}
                        disabled={invoicePagination.current >= invoicePagination.pages}
                        title="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setInvoicePagination({ ...invoicePagination, current: invoicePagination.pages })}
                        disabled={invoicePagination.current >= invoicePagination.pages}
                        title="Last page"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Transaction Logs Tab */}
      {activeTab === 'transactions' && (
        <>
          <div className="poss-filters-section">
            <div className="poss-filters-row">
              <div className="poss-filter-group">
                <label>Period</label>
                <select
                  value={transactionFilters.period}
                  onChange={(e) => setTransactionFilters({ ...transactionFilters, period: e.target.value })}
                >
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {transactionFilters.period === 'custom' && (
                <>
                  <div className="poss-filter-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={transactionFilters.startDate}
                      onChange={(e) => setTransactionFilters({ ...transactionFilters, startDate: e.target.value })}
                    />
                  </div>
                  <div className="poss-filter-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={transactionFilters.endDate}
                      onChange={(e) => setTransactionFilters({ ...transactionFilters, endDate: e.target.value })}
                    />
                  </div>
                </>
              )}
              <div className="poss-filter-group">
                <label>Type</label>
                <select
                  value={transactionFilters.type}
                  onChange={(e) => setTransactionFilters({ ...transactionFilters, type: e.target.value })}
                >
                  <option value="">All Types</option>
                  <option value="ACCRUAL">Service Charges</option>
                  <option value="REFUND">Refunds</option>
                  <option value="PAYMENT">Payments</option>
                  <option value="WAIVER">Waivers</option>
                </select>
              </div>
            </div>
          </div>

          {/* Transaction Summary */}
          {transactionSummary && (
            <div className="poss-stats-grid poss-transaction-summary">
              <div className="poss-stat-card">
                <div className="poss-stat-label">Total Accrued</div>
                <div className="poss-stat-value">{formatCurrency(transactionSummary.totalAccrued)}</div>
                <div className="poss-stat-subtext">{transactionSummary.accrualCount || 0} transactions</div>
              </div>
              <div className="poss-stat-card poss-stat-warning">
                <div className="poss-stat-label">Total Refunded</div>
                <div className="poss-stat-value">-{formatCurrency(transactionSummary.totalRefunded)}</div>
                <div className="poss-stat-subtext">{transactionSummary.refundCount || 0} refunds</div>
              </div>
              <div className="poss-stat-card poss-stat-success">
                <div className="poss-stat-label">Total Paid</div>
                <div className="poss-stat-value">{formatCurrency(transactionSummary.totalPaid)}</div>
                <div className="poss-stat-subtext">{transactionSummary.paymentCount || 0} payments</div>
              </div>
              <div className="poss-stat-card">
                <div className="poss-stat-label">Net for Period</div>
                <div className="poss-stat-value">{formatCurrency(transactionSummary.netOwed)}</div>
                <div className="poss-stat-subtext">Outstanding in period</div>
              </div>
            </div>
          )}

          <div className="poss-table-container">
            {loading ? (
              <div className="poss-loading-spinner">
                <RefreshCw size={24} className="spin" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="poss-empty-state">
                <Receipt size={48} />
                <p>No transactions found for this period</p>
              </div>
            ) : (
              <>
                <table className="poss-data-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Merchant</th>
                      <th>Type</th>
                      <th>Order</th>
                      <th>Order Total</th>
                      <th>Fee Rate</th>
                      <th>Amount</th>
                      <th>Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} className={tx.type === 'REFUND' ? 'poss-refunded-row' : ''}>
                        <td>
                          <div className="poss-merchant-cell">
                            <span className="poss-merchant-name">{formatDate(tx.createdAt)}</span>
                            <span className="poss-merchant-email">
                              {new Date(tx.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="poss-merchant-cell">
                            <span className="poss-merchant-name">{tx.merchantName || '-'}</span>
                            <span className="poss-merchant-email">{tx.merchantEmail || '-'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="poss-type-cell">
                            <span className={`poss-badge ${tx.type === 'ACCRUAL' ? 'poss-badge-pending' : tx.type === 'REFUND' ? 'poss-badge-overdue' : tx.type === 'PAYMENT' ? 'poss-badge-paid' : 'poss-badge-active'}`}>
                              {tx.type === 'ACCRUAL' && 'Service Charge'}
                              {tx.type === 'REFUND' && 'Refund'}
                              {tx.type === 'PAYMENT' && 'Payment'}
                              {tx.type === 'WAIVER' && 'Waiver'}
                            </span>
                            {(tx.type === 'PAYMENT' || tx.type === 'WAIVER') && tx.paymentMethod && (
                              <span
                                className={`poss-method-chip ${tx.paymentMethod === 'WALLET' ? 'poss-method-wallet' : 'poss-method-external'}`}
                                title={
                                  tx.paymentMethod === 'WALLET'
                                    ? 'Auto-paid from merchant wallet'
                                    : `Paid via ${tx.paymentMethod}`
                                }
                              >
                                {tx.paymentMethod === 'WALLET' ? 'Wallet' : tx.paymentMethod}
                              </span>
                            )}
                            {tx.type === 'ACCRUAL' && tx.walletAbsorbed === true && (
                              <span
                                className="poss-method-chip poss-method-wallet"
                                title="Wallet was debited at accrual time"
                              >
                                Wallet absorbed
                              </span>
                            )}
                            {tx.type === 'ACCRUAL' && tx.walletAbsorbed === false && (
                              <span
                                className="poss-method-chip poss-method-rejected"
                                title="Wallet parallel-write was rejected (overdraft cap or frozen wallet). Merchant owes this via invoice."
                              >
                                Wallet rejected
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{tx.orderNumber || '-'}</td>
                        <td>{tx.orderTotal ? formatCurrency(tx.orderTotal) : '-'}</td>
                        <td>{tx.feeRate ? `${(tx.feeRate * 100).toFixed(2)}%` : '-'}</td>
                        <td>
                          <span className={`poss-amount ${tx.amount < 0 ? 'negative' : tx.amount > 0 ? 'positive' : ''}`}>
                            {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                          </span>
                        </td>
                        <td>{formatCurrency(tx.balanceAfter)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transactionPagination.pages > 1 && (
                  <div className="poss-pagination">
                    <span className="poss-pagination-info">
                      Showing {((transactionPagination.current - 1) * 50) + 1} - {Math.min(transactionPagination.current * 50, transactionPagination.total)} of {transactionPagination.total} transactions
                    </span>
                    <div className="poss-pagination-buttons">
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setTransactionPagination({ ...transactionPagination, current: 1 })}
                        disabled={transactionPagination.current <= 1}
                        title="First page"
                      >
                        <ChevronsLeft size={16} />
                      </button>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setTransactionPagination({ ...transactionPagination, current: transactionPagination.current - 1 })}
                        disabled={transactionPagination.current <= 1}
                        title="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span className="poss-pagination-current">
                        Page {transactionPagination.current} of {transactionPagination.pages}
                      </span>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setTransactionPagination({ ...transactionPagination, current: transactionPagination.current + 1 })}
                        disabled={transactionPagination.current >= transactionPagination.pages}
                        title="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className="poss-btn poss-btn-sm poss-btn-secondary"
                        onClick={() => setTransactionPagination({ ...transactionPagination, current: transactionPagination.pages })}
                        disabled={transactionPagination.current >= transactionPagination.pages}
                        title="Last page"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      {showModal && (
        <div className="poss-modal-overlay" onClick={closeModal}>
          <div className="poss-modal" onClick={(e) => e.stopPropagation()}>
            {/* Detail Modal */}
            {modalType === 'detail' && merchantDetail && (
              <>
                <div className="poss-modal-header">
                  <h2><Eye size={20} /> Merchant Details</h2>
                  <button className="poss-modal-close" onClick={closeModal}><X size={20} /></button>
                </div>
                <div className="poss-modal-body">
                  <div className="poss-detail-section">
                    <h3>Merchant Info</h3>
                    <div className="poss-detail-grid">
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">Business Name</span>
                        <span className="poss-detail-value">{merchantDetail.merchant?.name || merchantDetail.merchant?.email?.split('@')[0] || '-'}</span>
                      </div>
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">Email</span>
                        <span className="poss-detail-value">{merchantDetail.merchant?.email}</span>
                      </div>
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">Phone</span>
                        <span className="poss-detail-value">{merchantDetail.merchant?.phone || '-'}</span>
                      </div>
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">POS Status</span>
                        <span className="poss-detail-value">
                          {merchantDetail.merchant?.isBlocked ? (
                            <span className="poss-badge poss-badge-blocked"><Ban size={12} /> Blocked</span>
                          ) : (
                            <span className="poss-badge poss-badge-active"><CheckCircle2 size={12} /> Active</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="poss-detail-section">
                    <h3>Balance Summary</h3>
                    <div className="poss-detail-grid">
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">Current Balance</span>
                        <span className="poss-detail-value poss-amount positive">{formatCurrency(merchantDetail.balance?.current)}</span>
                      </div>
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">Total Accrued</span>
                        <span className="poss-detail-value">{formatCurrency(merchantDetail.balance?.totalAccrued)}</span>
                      </div>
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">Total Paid</span>
                        <span className="poss-detail-value">{formatCurrency(merchantDetail.balance?.totalPaid)}</span>
                      </div>
                      <div className="poss-detail-item">
                        <span className="poss-detail-label">Total Waived</span>
                        <span className="poss-detail-value">{formatCurrency(merchantDetail.balance?.totalWaived)}</span>
                      </div>
                    </div>
                  </div>

                  {merchantDetail.invoices?.length > 0 && (
                    <div className="poss-detail-section">
                      <h3>Recent Invoices</h3>
                      <div className="poss-invoices-list">
                        {merchantDetail.invoices.map((inv) => (
                          <div key={inv.id} className="poss-invoice-item">
                            <div className="poss-invoice-info">
                              <span className="poss-invoice-number">{inv.invoiceNumber}</span>
                              <span className="poss-invoice-period">
                                {formatDate(inv.periodStart)} - {formatDate(inv.periodEnd)}
                              </span>
                            </div>
                            <div className="poss-invoice-actions">
                              <span className="poss-amount">{formatCurrency(inv.amount)}</span>
                              {renderStatusBadge(inv.status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="poss-modal-footer">
                  <button className="poss-btn poss-btn-secondary" onClick={closeModal}>Close</button>
                  {merchantDetail.merchant?.isBlocked ? (
                    <button
                      className="poss-btn poss-btn-success"
                      onClick={() => {
                        closeModal();
                        openActionModal('unblock', { merchantId: merchantDetail.merchant?.id, merchantName: merchantDetail.merchant?.name, balance: merchantDetail.balance?.current });
                      }}
                    >
                      <Unlock size={16} />
                      Unblock Merchant
                    </button>
                  ) : merchantDetail.balance?.current > 0 && (
                    <button
                      className="poss-btn poss-btn-danger"
                      onClick={() => {
                        closeModal();
                        openActionModal('block', { merchantId: merchantDetail.merchant?.id, merchantName: merchantDetail.merchant?.name, balance: merchantDetail.balance?.current });
                      }}
                    >
                      <Ban size={16} />
                      Block Merchant
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Block Merchant Modal */}
            {modalType === 'block' && selectedMerchant && (
              <>
                <div className="poss-modal-header">
                  <h2><Ban size={20} /> Block POS Access</h2>
                  <button className="poss-modal-close" onClick={closeModal}><X size={20} /></button>
                </div>
                <div className="poss-modal-body">
                  <div className="poss-alert poss-alert-warning">
                    <AlertTriangle size={18} />
                    <span>This will prevent the merchant from processing any POS orders until unblocked.</span>
                  </div>
                  <p>
                    Are you sure you want to block POS access for <strong>{selectedMerchant.merchantName || selectedMerchant.email}</strong>?
                  </p>
                  <p>Outstanding balance: <strong>{formatCurrency(selectedMerchant.balance)}</strong></p>
                  <div className="poss-form-group">
                    <label>Block Reason</label>
                    <textarea
                      value={actionNotes}
                      onChange={(e) => setActionNotes(e.target.value)}
                      placeholder="Enter reason for blocking..."
                    />
                  </div>
                </div>
                <div className="poss-modal-footer">
                  <button className="poss-btn poss-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button
                    className="poss-btn poss-btn-danger"
                    onClick={() => handleBlockMerchant(selectedMerchant.merchantId)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Blocking...' : 'Block Merchant'}
                  </button>
                </div>
              </>
            )}

            {/* Unblock Merchant Modal */}
            {modalType === 'unblock' && selectedMerchant && (
              <>
                <div className="poss-modal-header">
                  <h2><Unlock size={20} /> Restore POS Access</h2>
                  <button className="poss-modal-close" onClick={closeModal}><X size={20} /></button>
                </div>
                <div className="poss-modal-body">
                  <p>
                    Restore POS access for <strong>{selectedMerchant.merchantName || selectedMerchant.email}</strong>?
                  </p>
                  <p>Outstanding balance: <strong>{formatCurrency(selectedMerchant.balance)}</strong></p>
                  <div className="poss-form-group">
                    <label>Notes (Optional)</label>
                    <textarea
                      value={actionNotes}
                      onChange={(e) => setActionNotes(e.target.value)}
                      placeholder="Enter notes..."
                    />
                  </div>
                </div>
                <div className="poss-modal-footer">
                  <button className="poss-btn poss-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button
                    className="poss-btn poss-btn-success"
                    onClick={() => handleUnblockMerchant(selectedMerchant.merchantId)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Restoring...' : 'Restore Access'}
                  </button>
                </div>
              </>
            )}

            {/* Mark Paid Modal */}
            {modalType === 'markPaid' && selectedMerchant && (
              <>
                <div className="poss-modal-header">
                  <h2><CheckCircle2 size={20} /> Mark Invoice as Paid</h2>
                  <button className="poss-modal-close" onClick={closeModal}><X size={20} /></button>
                </div>
                <div className="poss-modal-body">
                  <p>
                    Mark invoice <strong>{selectedMerchant.invoiceNumber}</strong> as paid?
                  </p>
                  <p>Amount: <strong>{formatCurrency(selectedMerchant.amount)}</strong></p>
                  <div className="poss-form-group">
                    <label>Payment Reference</label>
                    <input
                      type="text"
                      value={actionNotes}
                      onChange={(e) => setActionNotes(e.target.value)}
                      placeholder="Enter payment reference..."
                    />
                  </div>
                </div>
                <div className="poss-modal-footer">
                  <button className="poss-btn poss-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button
                    className="poss-btn poss-btn-success"
                    onClick={() => handleMarkPaid(selectedMerchant.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Saving...' : 'Mark as Paid'}
                  </button>
                </div>
              </>
            )}

            {/* Send Reminder Modal */}
            {modalType === 'sendReminder' && selectedMerchant && (
              <>
                <div className="poss-modal-header">
                  <h2><Mail size={20} /> Send payment reminder</h2>
                  <button className="poss-modal-close" onClick={closeModal}><X size={20} /></button>
                </div>
                <div className="poss-modal-body">
                  <p>
                    Email a reminder to <strong>{selectedMerchant.merchantName || selectedMerchant.merchantEmail}</strong> about invoice <strong>{selectedMerchant.invoiceNumber}</strong>.
                  </p>
                  <p>Amount due: <strong>{formatCurrency(selectedMerchant.amount)}</strong></p>
                  <div className="poss-form-group">
                    <label>Add a personal note (optional)</label>
                    <textarea
                      value={reminderMessage}
                      onChange={(e) => setReminderMessage(e.target.value)}
                      placeholder="e.g. We've noticed your payout last week — please settle by Friday to keep POS active."
                      rows={4}
                      maxLength={500}
                    />
                    <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {reminderMessage.length}/500 characters. Leave blank for the standard reminder.
                    </small>
                  </div>
                </div>
                <div className="poss-modal-footer">
                  <button className="poss-btn poss-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button
                    className="poss-btn poss-btn-primary"
                    onClick={() => handleSendReminder(selectedMerchant.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Sending...' : 'Send reminder'}
                  </button>
                </div>
              </>
            )}

            {/* Waive Invoice Modal */}
            {modalType === 'waive' && selectedMerchant && (
              <>
                <div className="poss-modal-header">
                  <h2><XCircle size={20} /> Waive Invoice</h2>
                  <button className="poss-modal-close" onClick={closeModal}><X size={20} /></button>
                </div>
                <div className="poss-modal-body">
                  <div className="poss-alert poss-alert-warning">
                    <AlertTriangle size={18} />
                    <span>This will mark the invoice as waived and reduce the merchant's outstanding balance.</span>
                  </div>
                  <p>
                    Waive invoice <strong>{selectedMerchant.invoiceNumber}</strong>?
                  </p>
                  <p>Amount: <strong>{formatCurrency(selectedMerchant.amount)}</strong></p>
                  <div className="poss-form-group">
                    <label>Reason for Waiving *</label>
                    <textarea
                      value={waiveReason}
                      onChange={(e) => setWaiveReason(e.target.value)}
                      placeholder="Enter reason for waiving this invoice..."
                      required
                    />
                  </div>
                </div>
                <div className="poss-modal-footer">
                  <button className="poss-btn poss-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button
                    className="poss-btn poss-btn-warning"
                    onClick={() => handleWaiveInvoice(selectedMerchant.id)}
                    disabled={actionLoading || !waiveReason.trim()}
                  >
                    {actionLoading ? 'Waiving...' : 'Waive Invoice'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default POSSettlement;
